import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { LOG_LEVEL, Purchases } from '@revenuecat/purchases-capacitor';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import frFR from 'antd/locale/fr_FR';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import 'design/fonts.css';
import ScrollToTop from 'hooks/ScrollToTop';
import { locale as i18nLocale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from 'store/configureStore';
import Platform from 'utils/Platform';
import App from './App';
import { AuthProvider } from './auth/AuthContext';

const queryClient = new QueryClient();
// Register local notification listener as early as possible so cold-start notification taps are stored
import './pendingLaunchNotification';
import reportWebVitals from './reportWebVitals';

import 'dayjs/locale/fr';
import { antdTheme, applyCssVariables } from 'design';

dayjs.locale(i18nLocale);
dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(LocalizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

const SENTRY_BASE_CONF = {
  dsn: 'https://792a823ba039e63f000fb6863b0ca954@o4507486974377984.ingest.de.sentry.io/4507486992138320',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay
  replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
    // 401s are handled by the token-refresh interceptor in AuthContext; suppress them so transient
    // token-expiry errors that succeed on retry don't pollute Sentry.
    const err = hint?.originalException as any;
    if (err?.isAxiosError && err?.response?.status === 401 && !err?._reportToSentry) {
      return null;
    }
    return event;
  },
};
if (import.meta.env.VITE_API_SERVER_URL === 'https://api.chat.gyngercare.com/')
  Sentry.init({
    ...SENTRY_BASE_CONF,
    environment: 'production',
    tracePropagationTargets: [/^https:\/\/api.chat.gyngercare\.com\//],
  });
else if (import.meta.env.VITE_API_SERVER_URL === 'https://api.staging-briac.gyngercare.com/')
  Sentry.init({
    ...SENTRY_BASE_CONF,
    environment: 'staging',
    tracePropagationTargets: [/^https:\/\/api.staging-briac.gyngercare\.com\//],
  });

/**
 * Initialize the app with force update strategy.
 * Checks for updates and applies them immediately if available.
 * If no update is available, hides the splash screen.
 */
const initializeApp = async () => {
  if (Platform.isNative()) {
    try {
      const { nextBundleId } = await LiveUpdate.sync();
      if (nextBundleId) {
        // Reload the webview to apply the update immediately
        // This will cause the app to restart, so code after this won't execute
        await LiveUpdate.reload();
      }
    } catch (error) {
      // On error, hide splash screen to allow app to continue
      console.error('Error during LiveUpdate sync:', error);
    }
    await SplashScreen.hide();
  }
};
await initializeApp();

// Initialize Posthog
if (import.meta.env.VITE_POSTHOG_API_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    api_host: 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    mask_all_text: true,
    disable_session_recording: !import.meta.env.VITE_API_SERVER_URL.includes('gyngercare.com'),
    autocapture: import.meta.env.VITE_API_SERVER_URL.includes('gyngercare.com'),
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    before_send(event: any) {
      if (event?.properties.$ip) {
        event.properties.$ip = null;
      }

      if (event && import.meta.env.VITE_GIT_TAG) {
        event.properties.gynger_software_version = import.meta.env.VITE_GIT_TAG;
      }

      event.properties.gynger_platform = Capacitor.getPlatform();

      return event;
    },
  });
}

if (!import.meta.env.VITE_API_SERVER_URL.includes('gyngercare.com')) {
  posthog.featureFlags.overrideFeatureFlags({
    flags: {
      'homepage-social-links-display': true,
      // 'onboarding-slides-display': true,
      'prevention-program': 'control',
      'android-push-notification-channel': true,
      'android-local-notification-channel': true,
      'ios-notifications-better-settings': true,
      'in-app-chat-notification': true,
      'better-notification-request-timing': true,
      'no-guilt-after-ghost': true,
      // Pin the onboarding experiment to the legacy variant: the functional
      // suite asserts on the legacy carousel's copy. Without this the e2e runs
      // inherit whatever the live PostHog project rolls out, evaluated against
      // a user id that is freshly generated each run — a coin flip per run
      // rather than a test.
      'onboarding-slides-v3': 'old-slides',
    },
  });
}

// Configure RevenueCat Purchases
if (Platform.isNative()) {
  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({
      apiKey:
        {
          ios: 'appl_uRBiKUKJCPsKsqnBpgxSmpXMpOl',
          android: 'goog_DgOhmmBlhGcamDGdctEIgXFMwki',
          web: 'strp_wXnFHPcuBpKhmUDavcJLfnRxqvw',
        }[Platform.getPlatform()] || 'strp_wXnFHPcuBpKhmUDavcJLfnRxqvw',
    });
  } catch (error) {
    console.error('Error configuring RevenueCat Purchases', error);
  }
}

// Publish the design tokens as `--gg-*` custom properties before the first
// paint, so the few rules that have to live in real stylesheets can read them.
applyCssVariables();

// Clear the existing HTML content
document.body.innerHTML = '<div id="root"></div>';

const rootElement = document.getElementById('root');

const root = createRoot(rootElement!);

// Render your React component
// Note: If an update is available and reload() is called, the app will restart
// and this initialization will run again with the new bundle
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/*
            The theme lives in `design/antdTheme.ts`, derived from the token
            layer. Restyling an antd component belongs there — not in a
            `.ant-*` override in a component. See the note at the top of that
            file for the escalation order.
          */}
          <ConfigProvider locale={frFR} theme={antdTheme}>
            <ScrollToTop />
            <AuthProvider>
              <App />
            </AuthProvider>
          </ConfigProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
